import { useTranslation } from "react-i18next";
import FloatingTooltip from "@components/common/FloatingTooltip";
import TravelTimeline from "./TravelTimeline";
import { Journey, Transport } from "@gen/travel/v1/common_pb";
import { GhgTag } from "./GHGTag";

type TransportTimelineProps = {
  journey: Journey;
  carriers?: boolean;
  ghg?: boolean;
  showDuration?: boolean;
};

export default function TransportTimeline({
  journey,
  carriers,
  ghg,
  showDuration = true,
}: TransportTimelineProps) {
  const { t } = useTranslation();

  const transitCount = journey?.segments?.length - 1;

  function transporationIcons() {
    // for each segment, add the corresponding transport icon, avoiding duplicates.
    // fallback to marketing carrier if no transport icon is available.
    return journey.segments
      .reduce<{ name: string; logo: string }[]>((acc, segment) => {
        let name: string = "";
        let logo: string = "";
        if (segment.transport?.logo) {
          name = segment.transport?.name;
          logo = segment.transport?.logo;
        } else if (segment.operatingCarrier?.logo) {
          name = segment.operatingCarrier?.name;
          logo = segment.operatingCarrier?.logo;
        } else if (segment.marketingCarrier?.logo) {
          name = segment.marketingCarrier?.name;
          logo = segment.marketingCarrier?.logo;
        }
        const exists = acc.find((t) => t.name === name);
        if (!exists) {
          acc.push({ name, logo });
        }
        return acc;
      }, [])
      .map((t) => (
        <FloatingTooltip key={t.name} content={t.name}>
          {t.logo && (
            <img
              src={t.logo}
              alt="carrier-logo"
              className=" w-full max-w-[100%]"
            />
          )}
        </FloatingTooltip>
      ));
  }

  return (
    <div className="flex items-center min-w-0 grow space-x-1 sm:space-x-4 h-full max-h-full">
      <div className="flex flex-col items-center shrink-0 space-y-2 w-[40px] sm:w-[50px] whitespace-nowrap">
        {transporationIcons()}
        {transitCount > 0 && (
          <span className="text-xs text-center text-gray-600">
            {transitCount} {t("results.connections")}
          </span>
        )}
      </div>
      <TravelTimeline showDuration={showDuration} journey={journey}>
        {carriers || ghg ? (
          <div className="flex items-center">
            {carriers && <TransportationIcons journey={journey} />}
            {ghg && <GhgTag value={journey.ghgEmission} />}
          </div>
        ) : undefined}
      </TravelTimeline>
    </div>
  );
}

function TransportationIcons({ journey }: { journey: Journey }) {
  return journey.segments
    .map((s) => s.transport)
    .reduce<Transport[]>((acc, transport) => {
      if (transport && !acc.some((t) => t.name === transport.name)) {
        acc.push(transport);
      }
      return acc;
    }, [])
    .map((t) => (
      <div
        className="flex flex-col justify-center items-center text-center w-[50px] mr-2 text-[12px] font-normal"
        key={t.name}
      >
        <FloatingTooltip content={t.name}>
          {t.logo && <img src={t.logo} alt="carrier-logo" />}
        </FloatingTooltip>
      </div>
    ));
}
